import React from 'react';
import * as echarts from 'echarts';
import './RoomsManagementModule.less';
import { renderFormattedInt } from '@/utils/renderUtils';
import i2Math from '@/utils/i2Math';

/** 指挥中心卡片用：按门店 id 配置的物理房间总数（未配置视为 0） */
const STORE_ROOM_TOTAL_CAP = {
  '16': 79,
  '86': 34,
};

class RoomsManagementModule extends React.Component {
  constructor(props) {
    super(props);
    this.gaugeChartRef = React.createRef();
    this.chartInstanceRef = React.createRef();
  }

  /** 当前 query 对应门店在配置表中的房间总数，无则 0 */
  getDisplayStoreRoomTotalCap() {
    const rawId = this.props.queryManager?.currentQuery?.store_id;
    if (rawId === undefined || rawId === null || String(rawId).trim() === '') {
      return 0;
    }
    const key = String(rawId).trim();
    const cap = STORE_ROOM_TOTAL_CAP[key];
    return Number(cap) || 0;
  }

  /**
   * 开房数优先用指挥中心 unified fetch 写入的 GET /order-total/list totalRow（在住 checkInStatus=2）；
   * 无匹配请求结果或失败时退回门店房间状态 occupiedRooms。
   */
  getResolvedOccupiedRooms() {
    const { storeRoomStatusObj, queryManager, commandCenterOccupiedRoomsObj } = this.props;
    const data = storeRoomStatusObj?.data || {};
    const occupiedRoomsFromStatus = Number(data.occupiedRooms) || 0;
    const reduxQuery = commandCenterOccupiedRoomsObj?.query || {};
    const currentStoreId = queryManager?.currentQuery?.store_id;
    const reduxStoreId = reduxQuery?.storeId;
    const queryMatches =
      currentStoreId != null &&
      reduxStoreId != null &&
      String(currentStoreId).trim() === String(reduxStoreId).trim();
    const totalRow = commandCenterOccupiedRoomsObj?.data?.totalRow;
    const err = commandCenterOccupiedRoomsObj?.error;
    const hasOrderTotal =
      queryMatches &&
      err == null &&
      totalRow !== undefined &&
      totalRow !== null &&
      !Number.isNaN(Number(totalRow));
    if (hasOrderTotal) {
      return Number(totalRow) || 0;
    }
    return occupiedRoomsFromStatus;
  }

  // 初始化或更新图表
  initChart() {
    const { storeRoomStatusObj } = this.props;
    if (!storeRoomStatusObj || !storeRoomStatusObj.data) {
      return;
    }

    const { totalRooms: apiTotalRooms = 0, maintenanceRooms = 0 } = storeRoomStatusObj.data;
    const occupiedRooms = this.getResolvedOccupiedRooms();
    const configuredCap = this.getDisplayStoreRoomTotalCap();
    const totalForRate = configuredCap > 0 ? configuredCap : Number(apiTotalRooms) || 0;

    // 计算开房率：开房数 / (总数 - 报损数) * 100%
    // 报损的房间不能使用，所以要从总数中减去
    // 使用 i2Math 方法解决浮点数计算精度问题
    const availableRooms = i2Math.i2Sub(totalForRate, maintenanceRooms);
    const occupancyRate = availableRooms > 0 
      ? Math.round(i2Math.i2Multiply(i2Math.i2Division(occupiedRooms, availableRooms), 100)) 
      : 0;

    if (!this.chartInstanceRef.current && this.gaugeChartRef.current) {
    // 初始化图表
      const chart = echarts.init(this.gaugeChartRef.current);
      this.chartInstanceRef.current = chart;
    }

    if (this.chartInstanceRef.current && this.gaugeChartRef.current) {
      // 根据容器高度计算自适应字体大小
      const containerHeight = this.gaugeChartRef.current.offsetHeight || 200;
      const detailFontSize = Math.max(14, Math.min(32, containerHeight * 0.12)); // 自适应字体大小，范围14-32
      const titleFontSize = Math.max(10, Math.min(18, containerHeight * 0.08)); // 自适应标题字体大小，范围10-18
      const axisLineWidth = Math.max(4, Math.min(12, containerHeight * 0.04)); // 自适应线条宽度，范围4-12

      const darkHud = this.props.visualTheme === 'dark';
      const gaugeTextColor = darkHud ? '#e8eef5' : '#4a4a4a';
      const gaugeTrackColor = darkHud ? 'rgba(255, 255, 255, 0.14)' : '#e0e0e0';

      const option = {
        grid: {
          left: '0%',
          right: '0%',
          top: '0%',
          bottom: '35%', // 增加底部空间，避免文字重叠
          containLabel: false
        },
        series: [
          {
            name: '开房率',
            type: 'gauge',
            radius: '95%', // 稍微减小半径，为文字留出空间
            center: ['50%', '60%'], // 调整中心位置，让图表稍微上移
            startAngle: 180, // 起始角度（左侧）
            endAngle: 0, // 结束角度（右侧）
            min: 0,
            max: 100,
            splitNumber: 0, // 不显示分割线
            axisLine: {
              lineStyle: {
                width: axisLineWidth, // 自适应线条宽度
                color: [
                  [occupancyRate / 100, '#1890ff'], // 填充部分颜色
                  [1, gaugeTrackColor] // 背景部分颜色（深色 HUD 用浅透明轨）
                ]
              }
            },
            axisTick: {
              show: false // 不显示刻度
            },
            splitLine: {
              show: false // 不显示分割线
            },
            axisLabel: {
              show: false // 不显示标签
            },
            pointer: {
              show: false // 不显示指针
            },
            detail: {
              show: true,
              offsetCenter: [0, '15%'], // 调整文字位置，稍微上移
              fontSize: detailFontSize, // 自适应字体大小
              fontWeight: 700,
              color: gaugeTextColor,
              formatter: `${occupancyRate}%`
            },
            data: [
              {
                value: occupancyRate,
                name: '开房率',
                title: {
                  show: true,
                  offsetCenter: [0, '50%'], // 调整标题位置，稍微上移，确保不被截断
                  fontSize: titleFontSize, // 自适应标题字体大小
                  color: gaugeTextColor
                }
              }
            ]
          }
        ]
      };

      this.chartInstanceRef.current.setOption(option);
    }
  }

  componentDidMount() {
    this.initChart();

      // 处理窗口大小变化
    this.handleResize = () => {
      if (this.chartInstanceRef.current && this.gaugeChartRef.current) {
          // 重新计算自适应字体大小
        const containerHeight = this.gaugeChartRef.current.offsetHeight || 200;
          const detailFontSize = Math.max(14, Math.min(32, containerHeight * 0.12));
          const titleFontSize = Math.max(10, Math.min(18, containerHeight * 0.08));
          const axisLineWidth = Math.max(4, Math.min(12, containerHeight * 0.04));
          
          // 更新图表配置
        this.chartInstanceRef.current.setOption({
            series: [{
              axisLine: {
                lineStyle: {
                  width: axisLineWidth
                }
              },
              detail: {
                fontSize: detailFontSize
              },
              data: [{
                title: {
                  fontSize: titleFontSize
                }
              }]
            }]
          });
          
        this.chartInstanceRef.current.resize();
        }
      };

    window.addEventListener('resize', this.handleResize);
  }

  componentDidUpdate(prevProps) {
    // 当 props 数据变化时，更新图表
    if (prevProps.storeRoomStatusObj !== this.props.storeRoomStatusObj) {
      this.initChart();
    }
    if (prevProps.commandCenterOccupiedRoomsObj !== this.props.commandCenterOccupiedRoomsObj) {
      this.initChart();
    }
    const prevSid = prevProps.queryManager?.currentQuery?.store_id;
    const sid = this.props.queryManager?.currentQuery?.store_id;
    if (prevSid !== sid) {
      this.initChart();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    if (this.chartInstanceRef.current) {
      this.chartInstanceRef.current.dispose();
      this.chartInstanceRef.current = null;
        }
  }

  render() {
    const { storeRoomStatusObj } = this.props;
    const data = storeRoomStatusObj?.data || {};
    const { maintenanceRooms = 0 } = data;
    const displayRoomTotalCap = this.getDisplayStoreRoomTotalCap();
    const occupiedRooms = this.getResolvedOccupiedRooms();
  return (
    <div className="rooms-management-module">
      <div className="rooms-container">
        
        
        <div className="rooms-content">
          {/* 左侧：统计数据 */}
          
          <div className="rooms-stats">
            <div className="rooms-header">
              <div className="rooms-title">开房数</div>
            </div>
              <div className="rooms-total rooms-total-primary">{renderFormattedInt(occupiedRooms)}</div>
            <div className="rooms-sub-stats">
              <div className="rooms-stat-item">
                <span className="rooms-stat-label">房间总数: </span>
                  <span className="rooms-stat-value">{renderFormattedInt(displayRoomTotalCap)}</span>
              </div>
              <div className="rooms-stat-item">
                <span className="rooms-stat-label">报损: </span>
                  <span className="rooms-stat-value rooms-stat-value-red">{renderFormattedInt(maintenanceRooms)}</span>
              </div>
            </div>
          </div>

          {/* 右侧：仪表盘 */}
          <div className="rooms-gauge">
              <div ref={this.gaugeChartRef} className="rooms-gauge-chart"></div>
          </div>
        </div>
      </div>
    </div>
  );
  }
}

export default RoomsManagementModule;
